import { RecommendedPlace, Insight, VideoInfo, VaultItem, useWingmanStore } from '../store/useWingmanStore';
import { featureFlags } from './featureFlagService';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { ENV } from '../config/environment';

export enum ContentIntent {
    CINEMATIC = 'cinematic',
    EDUCATIONAL = 'educational',
    HISTORY = 'history',
    TRAVEL_GUIDE = 'travel_guide',
    FOOD = 'food',
    NIGHTLIFE = 'nightlife',
    LUXURY = 'luxury',
    ADVENTURE = 'adventure'
}

/**
 * 🛰️ InsightBridge
 * Best Practice Service for bridging the gap between Map Venues and Vibe Mode.
 */
class InsightBridgeService {
    private static instance: InsightBridgeService;
    private currentRequestId: number = 0;
    
    // 🧠 CACHE LAYER: 20 Minute TTL for discovery results
    private CACHE_TTL = 20 * 60 * 1000; 
    private videoCache: Map<string, { data: VideoInfo[], timestamp: number }> = new Map();

    private constructor() {}

    private getIntentQuery(venueName: string, intent: ContentIntent): string {
        const templates: Record<ContentIntent, string> = {
            [ContentIntent.CINEMATIC]: `${venueName} 4k cinematic atmosphere`,
            [ContentIntent.EDUCATIONAL]: `${venueName} educational documentary`,
            [ContentIntent.HISTORY]: `${venueName} history documentary`,
            [ContentIntent.TRAVEL_GUIDE]: `${venueName} travel guide things to do`,
            [ContentIntent.FOOD]: `${venueName} street food tour guide`,
            [ContentIntent.NIGHTLIFE]: `${venueName} nightlife and bars scene`,
            [ContentIntent.LUXURY]: `${venueName} luxury experience expensive`,
            [ContentIntent.ADVENTURE]: `${venueName} adventure travel hidden gems`
        };
        return templates[intent];
    }

    public static getInstance(): InsightBridgeService {
        if (!InsightBridgeService.instance) {
            InsightBridgeService.instance = new InsightBridgeService();
        }
        return InsightBridgeService.instance;
    }

    /**
     * 🧠 CONTEXT RECALL: Search the vault using natural language
     */
    public queryVault(query: string): VaultItem[] {
        // 🛡️ FEATURE FLAG: Vault
        if (!featureFlags.isEnabled('vault')) {
            console.log('[InsightBridge] Vault queries disabled by feature flag.');
            return [];
        }

        const { savedVideos } = useWingmanStore.getState();
        const searchLower = query.toLowerCase();
        
        return savedVideos.filter(v => 
            v.title.toLowerCase().includes(searchLower) || 
            v.category.toLowerCase().includes(searchLower) ||
            (v.summary && v.summary.toLowerCase().includes(searchLower))
        );
    }

    /**
     * 🧠 Converts raw search results into a cinematic Insight object.
     * Synchronous fallback for immediate UI response.
     */
    public bridgeVenuesToInsight(venues: RecommendedPlace[]): Insight {
        const videos: VideoInfo[] = venues.map(venue => ({
            title: venue.name,
            url: `https://vimeo.com/254516`, // Immediate placeholder
            thumbnail: `https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=800&q=80`,
            confidence: 0.9,
            coordinates: venue.coordinates,
            isPremium: true
        }));

        return {
            action: 'VIBE_CHECK',
            message: `Discovery loop initialized. Searching for viral content...`,
            priority: 1,
            videos: videos
        };
    }

    /**
     * 🛰️ YouTube Data Engine
     * Fetches real, location-targeted viral content for the venues.
     */
    public async fetchViralVideos(venues: RecommendedPlace[]): Promise<VideoInfo[]> {
        const requestId = ++this.currentRequestId;
        const gatewayUrl = ENV.GATEWAY_URL;

        const intents = [
            ContentIntent.TRAVEL_GUIDE, 
            ContentIntent.HISTORY, 
            ContentIntent.CINEMATIC,
            ContentIntent.FOOD,
            ContentIntent.EDUCATIONAL
        ];

        const videoPromises = venues.slice(0, 5).map(async (venue, index) => {
            // 🛡️ FEATURE FLAG: Video Engine
            if (!featureFlags.isEnabled('videoEngine')) {
                console.log('[InsightBridge] Video engine disabled by feature flag.');
                return null;
            }

            // 🧭 INTENT ENGINE: Rotate intents per query for balanced variety
            const intent = intents[index % intents.length];
            const query = this.getIntentQuery(venue.name, intent);

            // ⚡ CACHE CHECK
            const cached = this.videoCache.get(query);
            if (cached) {
                const age = Date.now() - cached.timestamp;
                if (age < this.CACHE_TTL) {
                    console.log(`[InsightBridge] Cache hit for: ${query} (Age: ${Math.round(age / 1000)}s)`);
                    return cached.data[0];
                }
                console.log(`[InsightBridge] Cache expired for: ${query}. Refreshing...`);
                this.videoCache.delete(query);
            }

            try {
                const response = await authenticatedFetch(`${gatewayUrl}/api/youtube/search`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ query })
                });

                if (!response.ok) {
                    throw new Error(`Gateway responded with status: ${response.status}`);
                }

                const data = await response.json();
                
                if (data.status === 'ERROR') {
                    console.warn(`[InsightBridge] Backend search error for ${query}:`, data.error);
                    return null;
                }

                if (data.items && data.items.length > 0) {
                    const video = data.items[0];

                    // 🧠 AI SUMMARY: Fetch a punchy one-sentence summary
                    let summary = "Professional travel intelligence layer with cinematic perspective.";
                    if (featureFlags.isEnabled('aiSummaries')) {
                        try {
                            const sumResponse = await authenticatedFetch(`${gatewayUrl}/api/youtube/summarize`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ 
                                    title: video.snippet.title, 
                                    description: video.snippet.description 
                                })
                            });
                            
                            if (sumResponse.ok) {
                                const sumData = await sumResponse.json();
                                if (sumData.status !== 'ERROR' && sumData.summary) {
                                    summary = sumData.summary;
                                }
                            }
                        } catch (sumError) {
                            console.warn("[InsightBridge] AI Summary fetch failed, using fallback:", sumError);
                        }
                    }

                    const videoInfo = {
                        title: venue.name,
                        url: `https://www.youtube.com/watch?v=${video.id.videoId}`,
                        thumbnail: video.snippet.thumbnails.high.url,
                        confidence: 1.0,
                        coordinates: venue.coordinates,
                        isPremium: true,
                        isLive: video.snippet.liveBroadcastContent === 'live',
                        category: intent,
                        videoId: video.id.videoId,
                        summary: summary
                    } as VideoInfo;

                    // ⚡ CACHE STORE
                    this.videoCache.set(query, { 
                        data: [videoInfo], 
                        timestamp: Date.now() 
                    });
                    return videoInfo;
                }
            } catch (e) {
                console.error(`[InsightBridge] YouTube search failed for ${venue.name}:`, e);
            }
            return null;
        });

        const results = await Promise.all(videoPromises);
        
        // 🛡️ GUARD: If a newer request has started, discard these results
        if (requestId !== this.currentRequestId) {
            console.warn(`[InsightBridge] Discarding stale response (ID: ${requestId}, Current: ${this.currentRequestId})`);
            return [];
        }

        return results.filter((v): v is VideoInfo => v !== null);
    }
}

export const insightBridge = InsightBridgeService.getInstance();
